import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { PageHeader, TableWrap, Th, Td, Loading, EmptyState, Badge, StatCard } from "../components/shared";
import { formatINR, formatDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Upload, Link2, Unlink, Trash2 } from "lucide-react";

const STATUS_CLS = {
  Matched: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Manually Matched": "bg-blue-50 text-blue-700 border-blue-200",
  Unmatched: "bg-amber-50 text-amber-700 border-amber-200",
};

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const di = header.findIndex((h) => h.includes("date"));
  const ri = header.findIndex((h) => h.includes("ref"));
  const ai = header.findIndex((h) => h.includes("amount") || h.includes("amt"));
  const desci = header.findIndex((h) => h.includes("desc") || h.includes("narration") || h.includes("particular"));
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(",");
    const amtRaw = (cols[ai] || "0").replace(/[₹",\s]/g, "");
    let dateVal = (cols[di] || "").trim();
    // normalize DD-MM-YYYY or DD/MM/YYYY to YYYY-MM-DD
    const m = dateVal.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
    if (m) { const yy = m[3].length === 2 ? "20" + m[3] : m[3]; dateVal = `${yy}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`; }
    rows.push({ date: dateVal, bank_ref: (cols[ri] || "").trim(), amount: parseFloat(amtRaw) || 0, description: (cols[desci] || "").trim() });
  }
  return rows.filter((r) => r.date && r.amount);
}

export default function Reconciliation() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [data, setData] = useState({ entries: [], system_transactions: [] });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { api.get("/accounts").then((r) => { setAccounts(r.data); if (r.data.length && !accountId) setAccountId(r.data[0].id); }); }, []);

  const load = useCallback(() => {
    if (!accountId) return;
    setLoading(true);
    api.get(`/reconciliation?account_id=${accountId}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [accountId]);
  useEffect(() => { load(); }, [load]);

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { const rows = parseCSV(reader.result); if (!rows.length) return toast.error("No valid rows found. Expect columns: Date, Reference, Amount, Description"); setPreview(rows); };
    reader.readAsText(file);
    e.target.value = "";
  };

  const doImport = async () => {
    try {
      const { data: res } = await api.post("/reconciliation/import", { account_id: accountId, rows: preview });
      toast.success(`Imported ${res.imported} rows · ${res.auto_matched} auto-matched`);
      setPreview(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const match = async (eid, txnId) => { if (!txnId) return; try { await api.post(`/reconciliation/${eid}/match`, { txn_id: txnId }); load(); } catch (e) { toast.error(apiError(e)); } };
  const unmatch = async (eid) => { try { await api.post(`/reconciliation/${eid}/unmatch`); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (eid) => { try { await api.delete(`/reconciliation/${eid}`); load(); } catch (e) { toast.error(apiError(e)); } };

  const matched = data.entries.filter((e) => e.match_status !== "Unmatched").length;
  const matchedAmt = data.entries.filter((e) => e.match_status !== "Unmatched").reduce((s, e) => s + e.amount, 0);
  const unmatchedAmt = data.entries.filter((e) => e.match_status === "Unmatched").reduce((s, e) => s + e.amount, 0);
  const stmtNet = data.entries.reduce((s, e) => s + e.amount, 0);
  const acct = accounts.find((a) => a.id === accountId);
  const sysBal = acct?.balance || 0;
  const matchPct = data.entries.length ? Math.round((matched / data.entries.length) * 100) : 0;

  return (
    <div>
      <PageHeader title="Bank Reconciliation" subtitle="Import a bank statement CSV and match it against system transactions" />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select data-testid="recon-account" value={accountId} onChange={(e) => setAccountId(e.target.value)} className="h-10 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-sm">
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.type})</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-sm bg-[#0F2942] text-white hover:bg-[#163a5c] rounded-lg px-4 h-10 cursor-pointer">
          <Upload className="h-4 w-4" /> Import CSV
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="recon-upload" />
        </label>
        <span className="text-xs text-slate-400">CSV columns: Date, Reference, Amount, Description</span>
      </div>

      {preview && (
        <div className="bg-white dark:bg-slate-900 border border-teal-200 rounded-xl p-4 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading font-semibold text-sm">Preview — {preview.length} rows</h3>
            <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => setPreview(null)}>Cancel</Button>
              <Button size="sm" onClick={doImport} data-testid="recon-confirm-import" className="bg-teal-600 hover:bg-teal-700">Import {preview.length} rows</Button></div>
          </div>
          <div className="max-h-48 overflow-y-auto text-xs">
            <table className="w-full"><thead><tr className="text-slate-400"><td className="py-1">Date</td><td>Ref</td><td className="text-right">Amount</td><td>Description</td></tr></thead>
              <tbody>{preview.slice(0, 50).map((r, i) => <tr key={i} className="border-t border-slate-100"><td className="py-1">{formatDate(r.date)}</td><td>{r.bank_ref}</td><td className="text-right font-mono">{formatINR(r.amount)}</td><td className="truncate max-w-[240px]">{r.description}</td></tr>)}</tbody></table>
          </div>
        </div>
      )}

      {loading ? <Loading /> : data.entries.length === 0 ? <EmptyState title="No statement rows imported" sub="Upload a CSV to begin reconciliation." /> : (
        <>
          <div className="text-sm text-slate-500 mb-2">{matched} of {data.entries.length} matched</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <StatCard label={`Matched (${matched})`} value={matchedAmt} accent="emerald" testid="recon-matched" />
            <StatCard label={`Unmatched (${data.entries.length - matched})`} value={unmatchedAmt} accent="amber" testid="recon-unmatched" />
            <StatCard label="System Balance" value={sysBal} accent="blue" />
            <StatCard label="Difference (Sys − Stmt)" value={sysBal - stmtNet} accent={Math.abs(sysBal - stmtNet) < 0.01 ? "emerald" : "rose"} sub={Math.abs(sysBal - stmtNet) < 0.01 ? "Reconciled ✓" : "Review needed"} />
          </div>
          <div className="h-2 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden mb-4">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${matchPct}%` }} data-testid="recon-progress" />
          </div>
          <TableWrap>
            <thead><tr><Th>Date</Th><Th>Bank Ref</Th><Th className="text-right">Amount</Th><Th>Description</Th><Th>Status</Th><Th>System Txn</Th><Th></Th></tr></thead>
            <tbody>{data.entries.map((e) => (
              <tr key={e.id} data-testid={`recon-row-${e.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                <Td>{formatDate(e.date)}</Td><Td className="font-mono">{e.bank_ref || "-"}</Td>
                <Td className="text-right font-mono">{formatINR(e.amount)}</Td>
                <Td className="max-w-[220px] truncate">{e.description || "-"}</Td>
                <Td><Badge cls={STATUS_CLS[e.match_status]}>{e.match_status}</Badge></Td>
                <Td>
                  {e.matched_txn_number ? <span className="font-mono text-xs">{e.matched_txn_number}</span> : (
                    <select data-testid={`recon-match-${e.id}`} defaultValue="" onChange={(ev) => match(e.id, ev.target.value)} className="h-8 rounded border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-1 text-xs max-w-[200px]">
                      <option value="">Match to…</option>
                      {data.system_transactions.map((t) => <option key={t.id} value={t.id}>{t.txn_number} · {formatINR(t.money_in || t.money_out || t.amount)} · {formatDate(t.txn_date)}</option>)}
                    </select>
                  )}
                </Td>
                <Td><div className="flex gap-2">
                  {e.matched_txn_id && <button onClick={() => unmatch(e.id)} data-testid={`recon-unmatch-${e.id}`} className="text-amber-500 hover:text-amber-700" title="Unmatch"><Unlink className="h-3.5 w-3.5" /></button>}
                  <button onClick={() => del(e.id)} className="text-rose-400 hover:text-rose-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </div></Td>
              </tr>
            ))}</tbody>
          </TableWrap>
        </>
      )}
    </div>
  );
}
